import type { ReactElement } from 'react';
import { useRef, useCallback, useState } from 'react';

import type { CountryCode } from '@hotjar/country-lib';
import { useTranslation } from '@hotjar/translations';
import type { CallbackSuccess, CallbackResponse, CallbackFailure } from './hooks/useZuoraLibrary';
import type { CallbackState } from './utils/emitCallbackEvents';
import type { PAGE } from '~/subscription-management/utils/tracking';
import {
  FORM_NAME,
  PAYMENT_DETAILS_PAGE,
  trackClick,
  trackFormSubmit,
} from '~/subscription-management/utils/tracking';
import { ZuoraPaymentForm } from './ZuoraPaymentForm';
import { ErrorMessage } from './ErrorMessage';

export interface VaultedResponse {
  refId: string;
}

export type OnErrorResponse = {
  type: Extract<CallbackState, 'failed_load' | 'failed_submission'>;
} & CallbackFailure;

const DEFAULT_ERROR_BATCH = {
  timeout: null,
  message: '',
  code: '',
};

interface CreditCardDetailsProps {
  customerId?: number;
  countryCode?: CountryCode;
  onSuccess(response: VaultedResponse): void;
  uiPosition: PAGE;
  accountId: number;
}

export const CreditCardDetails = ({
  customerId,
  countryCode,
  onSuccess,
  uiPosition,
  accountId,
}: CreditCardDetailsProps): ReactElement => {
  const errorBatch = useRef<{
    timeout: ReturnType<typeof setTimeout> | null;
    message: string;
    code: string;
  }>(DEFAULT_ERROR_BATCH);
  const [error, setError] = useState<string | undefined>();
  const [showRetry, setShowRetry] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [showForm, setShowForm] = useState(true);
  const { t } = useTranslation('checkout', { keyPrefix: 'payment-details' });

  const setErrorToDisplay = (message: string | undefined) => {
    const canRetry = (message ?? '').includes('[Attempt_Exceed_Limitation]') && retryCount < 3;
    setShowRetry(canRetry);
    setError(canRetry ? t('error-attempt-exceed-limitation') : message);
  };

  /**
   * FIXME: `reloadForm` is a horrible hack together with the `showForm`, `showRetry`, `retryCount` and all the crap in this components
   * It mitigates the zuora error of `[Attempt_Exceed_Limitation]` while we are busy trying to sort this out with Zuora support
   * Do not dare to hope, for it is forsaken in these parts
   */
  const reloadForm = () => {
    setShowForm(false);
    setErrorToDisplay(undefined);
    /**
     * we only allow 3 retries to keep the fraudulent bastards out
     */
    setRetryCount((count) => count + 1);

    setTimeout(() => {
      setShowForm(true);
    });
  };

  const track = useCallback(
    (isSuccessful: boolean, errorMessage?: string, errorCode?: string) => {
      if (accountId !== undefined) {
        trackClick({
          eventName: PAYMENT_DETAILS_PAGE.save,
          uiPosition,
          accountId,
        });
        trackFormSubmit({
          name: FORM_NAME.updatePayment,
          submitSuccess: isSuccessful,
          accountId,
          errorMessage,
          errorCode,
        });
      }
    },
    [accountId, uiPosition],
  );

  // All client errors are thrown individually per field.
  // This method allows is to only track this once.
  const trackErrorsInBatch = useCallback(
    (message: string, code?: string) => {
      if (errorBatch.current.timeout !== null) {
        clearTimeout(errorBatch.current.timeout);
      }

      const prevMessage = errorBatch.current.message;
      const newMessage = `${prevMessage}${prevMessage ? ', ' : ''}${message}`;
      errorBatch.current = {
        message: newMessage,
        code: code ?? '',
        timeout: setTimeout(() => {
          track(false, newMessage, code);
          // eslint-disable-next-line no-console
          console.error(newMessage);
          errorBatch.current = DEFAULT_ERROR_BATCH;
        }, 100),
      };
    },
    [track],
  );

  const handleCallbackErrors = (err: OnErrorResponse['type']) => {
    const message = err === 'failed_submission' ? t('error-saving-payment') : t('error-generic');
    setErrorToDisplay(message);
  };

  const handleCallbackEvents = (state: CallbackState, response: CallbackResponse) => {
    errorBatch.current = DEFAULT_ERROR_BATCH;
    setErrorToDisplay(undefined);
    const callbackError = state !== 'success' ? state : undefined;
    track(state === 'success', callbackError);

    return state === 'success'
      ? onSuccess({ refId: (response as CallbackSuccess).refId })
      : handleCallbackErrors(state);
  };

  return (
    <>
      <ErrorMessage errorMessage={error} showRetry={showRetry} retry={reloadForm} />
      {/* 
        FIXME: you can read my comments a little higher up, but hiding and showing the form is the fastest, easiest and dirtiest way to reload it 
      */}
      {showForm && (
        <ZuoraPaymentForm
          customerId={customerId}
          countryCode={countryCode ?? 'US'}
          handleCallbackEvents={handleCallbackEvents}
          track={trackErrorsInBatch}
          displayError={setErrorToDisplay}
        />
      )}
    </>
  );
};
